import { supabase } from "./supabase";
import { shuffleInPlace } from "./utils/shuffle";
import type { Json } from "@/types/database";
import type { HomepageCollection } from "@/types/homepage";

/**
 * Collections: public playlists our search engine builds every week from that week's NEW sync
 * briefs (buyer home spec 2026-10-26, §4.3, §4.13 and §14 "Collections v2"). A Collection is a
 * `playlists` row with a `collection_key`; `collection_listed` marks the current week's set (the
 * weekly switch flips last week's off and this week's on in one transaction; last week's stay
 * reachable by link, off the shelf). The refresh writes `search_metadata.refreshed_at` and
 * `.sort_order` (the brief's place in the week). How many tracks it holds is counted live.
 *
 * Before the engine's v2 migration there is no `collection_listed` column, so the read fails:
 * every caller then draws nothing (the home shelf) or says it could not load (/collections).
 */

/** A Collection with fewer tracks than this is left out: too thin to start a brief from. */
export const MIN_COLLECTION_TRACKS = 10;

/** How many Collections the home shelf shows on a visit. */
export const COLLECTIONS_SHOWN = 10;

function metadataField(metadata: Json | null, key: string): Json | undefined {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return undefined;
  return metadata[key];
}

/**
 * This week's usable Collections, in the briefs' order. One read of at most ~40 rows; the week is
 * chosen by the server (`collection_listed`), never filtered here. Throws the query's error: the
 * caller decides that a failure draws nothing.
 */
export async function fetchCollections(): Promise<HomepageCollection[]> {
  const { data, error } = await supabase
    .from("playlists")
    // The live count, under the viewer's RLS: what /playlist/:id will list. `track_count` in
    // search_metadata is the build's snapshot, and a track a label moves back to drafts leaves
    // every playlist at once (trg_remove_unpublished_from_playlists), so it can say 50 over 5.
    .select("id, name, cover_image_url, search_metadata, playlist_tracks(count)")
    .not("collection_key", "is", null)
    // The current week's set only: last week's lists stay public (reachable by link) but unlisted.
    .eq("collection_listed", true)
    // RLS also returns a playlist that is merely shared by link; a Collection the engine has
    // retired is set private, and must not come back through an old link.
    .eq("is_public", true)
    // `->` keeps the jsonb number, so 10 sorts after 2 (`->>` would compare text).
    .order("search_metadata->sort_order", { ascending: true })
    .order("id", { ascending: true });
  if (error) throw error;

  return (data ?? []).flatMap((row) => {
    const trackCount = row.playlist_tracks?.[0]?.count ?? 0;
    if (trackCount < MIN_COLLECTION_TRACKS) return [];
    const refreshedAt = metadataField(row.search_metadata, "refreshed_at");
    return [
      {
        id: row.id,
        name: row.name,
        // Written ready to render (the engine's contract, enforced by a CHECK): a root-relative
        // path to one of the repo's non-copyright photos, a URL in our own `artwork` bucket, or null.
        coverImageUrl: row.cover_image_url?.trim() || null,
        trackCount,
        refreshedAt: typeof refreshedAt === "string" && refreshedAt ? refreshedAt : null,
      },
    ];
  });
}

/**
 * The Collections a visit shows: COLLECTIONS_SHOWN picked at random from the week's set (fewer:
 * all of them, in random order). The caller picks once per mount, so a re-render or a refetch
 * never reshuffles the shelf under the buyer.
 */
export function pickCollections(collections: readonly HomepageCollection[]): HomepageCollection[] {
  return shuffleInPlace([...collections]).slice(0, COLLECTIONS_SHOWN);
}

/**
 * When the oldest of the given Collections was built, or null when none says. The shelf dates the
 * week's set by this: if one list is older (a partial run), the line never implies they are all
 * newer than they are. Each playlist's own page keeps its own date.
 */
export function oldestCollectionRefresh(collections: readonly HomepageCollection[]): Date | null {
  let oldest: number | null = null;
  for (const { refreshedAt } of collections) {
    const time = refreshedAt ? Date.parse(refreshedAt) : NaN;
    if (!Number.isNaN(time) && (oldest === null || time < oldest)) oldest = time;
  }
  return oldest === null ? null : new Date(oldest);
}
